"use client";

import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Button } from "../../../ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../ui/select";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import useProjectManagement from "../../../../hooks/useProjectManagement";
import { CreateProjectData } from "../../../../types/projectmanagement";
import { useToast } from "../../../ui/use-toast";
import { CHART_COLORS } from "../../../../constants/colors";

interface Project {
  id: number;
  project_name: string;
  display_name?: string;
}

interface ShadcnProjectSelectorProps {
  selectedProject: string | null;
  onProjectChange: (projectId: string | null) => void;
  onProjectCreated?: (project: any) => void;
}

export default function ShadcnProjectSelector({
  selectedProject,
  onProjectChange,
  onProjectCreated,
}: ShadcnProjectSelectorProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    provider: "",
    projectId: "",
    region: "",
  });

  const { projects, isLoading, error, createProject, isCreating } =
    useProjectManagement();
  const { toast } = useToast();

  const dropdownProjects = projects.map((p: Project) => ({
    id: p.id.toString(),
    name: p.display_name || p.project_name,
  }));

  const allFieldsFilled =
    !!formData.name &&
    !!formData.provider &&
    !!formData.region &&
    (formData.provider === "gcp" ? !!formData.projectId : true);

  useEffect(() => {
    if (!dialogOpen) {
      setFormData({ name: "", provider: "", projectId: "", region: "" });
    }
  }, [dialogOpen]);

  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (value: string) => {
    if (value === "__create__") {
      setDialogOpen(true);
    } else {
      onProjectChange(value || null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allFieldsFilled) return;
    try {
      const data: CreateProjectData = {
        project_name: formData.name,
        display_name: formData.name,
        primary_provider: formData.provider,
        default_region: formData.region,
        description: `Created via AI Chat`,
      };
      const newProject = await createProject(data);
      toast({
        title: "Project Created Successfully!",
        description: newProject.display_name || newProject.project_name,
        variant: "default",
      });

      onProjectCreated?.(newProject);
      onProjectChange(newProject.id.toString());
      setDialogOpen(false);
    } catch (err) {
      toast({
        title: "Error Creating Project",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const providerOptions = [
    { value: "aws", label: "Amazon Web Services", icon: "/cloud2.svg" },
    { value: "gcp", label: "Google Cloud Platform", icon: "/cloud1.svg" },
    { value: "azure", label: "Microsoft Azure", icon: "/cloud3.svg" },
  ];

  return (
    <>
      <Select value={selectedProject || ""} onValueChange={handleSelectChange}>
        <SelectTrigger
          className={`w-[200px] h-8 text-sm bg-[#F1F1F1] border-0 focus:ring-0 focus:outline-none ${
            selectedProject ? "text-black" : "text-[#868686]"
          }`}
        >
          <SelectValue placeholder="No project selected" />
        </SelectTrigger>
        <SelectContent className="w-[200px] bg-white border border-gray-200 shadow-lg">
          <div className="max-h-[120px] overflow-y-auto">
            {isLoading ? (
              <div className="px-2 py-1.5 text-sm text-gray-500">
                Loading projects...
              </div>
            ) : error ? (
              <div className="px-2 py-1.5 text-sm text-red-500">
                Error loading projects
              </div>
            ) : dropdownProjects.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-gray-500">
                No projects found
              </div>
            ) : (
              dropdownProjects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))
            )}
          </div>
          <SelectItem value="__create__" className="border-t mt-1 pt-1">
            <div className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded-sm">
              <Plus className="w-3 h-3" /> Create new project
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Create a new project with your cloud provider settings.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                placeholder="Enter project name"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="provider">Provider</Label>
              <Select
                value={formData.provider}
                onValueChange={(v) => handleFormChange("provider", v)}
              >
                <SelectTrigger className="w-full h-10 px-3 py-2 text-sm border border-input bg-background">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providerOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <img
                          src={opt.icon}
                          alt={opt.label}
                          className="w-4 h-4"
                        />
                        <span>{opt.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.provider === "gcp" && (
              <div className="grid gap-2">
                <Label htmlFor="projectId">Project ID</Label>
                <Input
                  id="projectId"
                  value={formData.projectId}
                  onChange={(e) =>
                    handleFormChange("projectId", e.target.value)
                  }
                  placeholder="Enter project ID"
                />
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={formData.region}
                onChange={(e) => handleFormChange("region", e.target.value)}
                placeholder="Enter region"
              />
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={!allFieldsFilled || isCreating}
                style={{
                  backgroundColor:
                    allFieldsFilled && !isCreating
                      ? CHART_COLORS.PRIMARY_GREEN
                      : "#F1F1F1",
                  color:
                    allFieldsFilled && !isCreating ? "#fff" : "#868686",
                }}
              >
                {isCreating ? "Creating..." : "Create Project"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
